import {
  forwardRef,
  memo,
  type CSSProperties,
  type ReactNode,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {isFirefox, isMobile} from 'react-device-detect';
import ReactWebcam from 'react-webcam';
import {cn} from '../lib/cn';
import {compressFile} from '../lib/compression';
import type {VideoConstraints, WebcamRefTypes} from './types';
import {useErrorHandler, type WebcamErrorDetails} from './useErrorHandler';
import {
  getMobileOrDesktopVideoConstraints,
  getPhotoCanvas,
  getReducedVideoConstraints,
  isMobileOrTablet,
} from './utils';
import {getCanvasBlob, getFileSizeMB, toBase64} from '../lib/utils';

const SCREENSHOT_FORMAT = 'image/jpeg';
const mirroredTransformStyle: CSSProperties = {transform: 'rotateY(180deg)'};

export type WebcamProps = Partial<ReactWebcam> & {
  onDisabled?: (value: boolean) => void;
  onUserMedia?: () => void;
  imageSmoothing?: boolean;
  mirrored?: boolean;
  videoConstraints?: VideoConstraints;
  additionalConstraints?: VideoConstraints;
  hideScanArea?: boolean;
  compression?: boolean;
  roundedScanArea?: boolean;
  cropToScanArea?: boolean;
  isLoading?: boolean;
  placeholderImage?: ReactNode;
  children?: ReactNode;
  className?: string;
  minScreenshotH?: number;
  minScreenshotW?: number;
  width?: number;
  height?: number;
  screenshotQuality?: number;
  screenshotMaxSize?: number;
  videoSourceDeviceId?: string;
  onError: (errorDetails: WebcamErrorDetails) => void;
};

const Webcam = memo(
  forwardRef<WebcamRefTypes, WebcamProps>(
    (
      {
        onUserMedia,
        onDisabled,
        imageSmoothing,
        videoConstraints,
        additionalConstraints,
        hideScanArea,
        roundedScanArea,
        cropToScanArea,
        className,
        mirrored,
        children,
        isLoading,
        placeholderImage,
        screenshotMaxSize,
        minScreenshotH,
        minScreenshotW,
        videoSourceDeviceId,
        screenshotQuality = 1,
        compression,
        onError,
        ...props
      },
      ref,
    ) => {
      const [minScreenshotHeight, setMinScreenshotHeight] = useState<number>();
      const [minScreenshotWidth, setMinScreenshotWidth] = useState<number>();
      const [useReducedConstraints, setUseReducedConstraints] = useState(false);
      const [numberOfCameras, setNumberOfCameras] = useState(0);

      const constraints = useMemo(() => {
        if (videoConstraints) {
          return videoConstraints;
        }

        if (useReducedConstraints) {
          return getReducedVideoConstraints({
            deviceId: videoSourceDeviceId,
            additional: additionalConstraints,
          });
        }

        return getMobileOrDesktopVideoConstraints({
          deviceId: videoSourceDeviceId,
          additional: additionalConstraints,
        });
      }, [
        additionalConstraints,
        useReducedConstraints,
        videoConstraints,
        videoSourceDeviceId,
      ]);

      const isMirroredInCSS = mirrored === undefined && !isMobileOrTablet();
      const webcamRef = useRef<ReactWebcam>(null);
      const scanAreaRef = useRef<HTMLDivElement>(null);
      const canvasRef = useRef<HTMLCanvasElement>(null);
      const containerRef = useRef<HTMLDivElement>(null);

      const {handleUserMediaError} = useErrorHandler({
        onError: errorDetails => {
          onDisabled?.(true);
          onError(errorDetails);
        },
      });

      const handleUserMediaErrorWithFallback = useCallback(
        (error: string | DOMException | Error) => {
          if (
            error instanceof DOMException &&
            error.name === 'OverconstrainedError' &&
            !useReducedConstraints &&
            !videoConstraints
          ) {
            console.warn('Camera constraints failed, trying reduced constraints:', error);
            setUseReducedConstraints(true);
            return;
          }

          handleUserMediaError(error);
        },
        [handleUserMediaError, useReducedConstraints, videoConstraints],
      );

      const compressImage = async (canvas?: HTMLCanvasElement) => {
        if (!canvas) return;

        const blobImage = await getCanvasBlob(canvas);
        if (!blobImage) return;

        const compressedFile = await compressFile(blobImage, {
          convertSize: screenshotMaxSize,
        });

        // eslint-disable-next-line no-console
        console.log(
          'Before: ',
          getFileSizeMB(blobImage)?.toFixed(2),
          'MB',
          'After: ',
          getFileSizeMB(compressedFile)?.toFixed(2),
          'MB',
        );

        return toBase64(compressedFile);
      };

      const handleTakePhoto = () => {
        const takenPhotoCanvas = getPhotoCanvas({
          player: webcamRef.current?.video,
          container: containerRef.current,
          canvas: canvasRef.current,
          scanArea: scanAreaRef.current,
          cropToScanArea,
        });

        if (!takenPhotoCanvas) return;
        if (compression) return compressImage(takenPhotoCanvas);

        return Promise.resolve(
          takenPhotoCanvas.toDataURL(SCREENSHOT_FORMAT, screenshotQuality),
        );
      };

      useImperativeHandle(
        ref,
        () =>
          ({
            takePhoto: handleTakePhoto,
            numberOfCameras,
            ...webcamRef.current,
          }) as WebcamRefTypes,
      );

      const handleUserMediaSuccess = useCallback(
        (stream: MediaStream) => {
          if (!isMobile) {
            const track = stream.getVideoTracks()[0];
            const capabilities = !isFirefox && track?.getCapabilities();

            if (capabilities) {
              setMinScreenshotHeight(capabilities.height?.max);
              setMinScreenshotWidth(capabilities.width?.max);
            }
          } else {
            setMinScreenshotWidth(minScreenshotW);
            setMinScreenshotHeight(minScreenshotH);
          }

          navigator.mediaDevices
            .enumerateDevices()
            .then(devices => {
              setNumberOfCameras(
                devices.filter(device => device.kind === 'videoinput').length,
              );
            })
            .catch(error => {
              console.warn('Failed to enumerate devices:', error);
              setNumberOfCameras(1);
            });

          onUserMedia?.();
        },
        [minScreenshotH, minScreenshotW, onUserMedia],
      );

      return (
        <div
          ref={containerRef}
          className={cn(
            'webcam relative z-[1] mx-auto flex h-[222px] w-[332px] items-center justify-center overflow-hidden rounded-[6px] text-center',
            'kiosko:h-[620px] kiosko:max-h-[620px] kiosko:w-[890px] kiosko:max-w-[890px]',
            className,
          )}
          style={isMirroredInCSS ? mirroredTransformStyle : undefined}
        >
          {!hideScanArea && (
            <div
              ref={scanAreaRef}
              className={cn(
                'webcam__scan-area absolute inset-0 z-[2] m-auto flex w-[calc(100%-32px)] items-center justify-center shadow-[0_0_0_9999em_rgb(0_0_0_/_60%)]',
                'h-[200px] rounded-[15px] min-[479px]:h-[calc(100%-32px)]',
                roundedScanArea &&
                  'h-[197px] w-[165px] rounded-[45%] border-[3px] border-white shadow-[0_0_8px_#fff,inset_0_0_0_#fff,0_0_0_9999em_rgb(0_0_0_/_60%)]',
                'kiosko:h-[calc(100%-170px)] kiosko:w-[345px]',
              )}
              style={isMirroredInCSS ? mirroredTransformStyle : undefined}
            >
              {!roundedScanArea && (
                <>
                  <div className="absolute left-[-2px] top-[-2px] h-[45px] w-[45px] rounded-tl-[15px] border-[4px] border-b-0 border-r-0 border-white" />
                  <div className="absolute right-[-2px] top-[-2px] h-[45px] w-[45px] rounded-tr-[15px] border-[4px] border-b-0 border-l-0 border-white" />
                  <div className="absolute bottom-[-2px] right-[-2px] h-[45px] w-[45px] rounded-br-[15px] border-[4px] border-l-0 border-t-0 border-white" />
                  <div className="absolute bottom-[-2px] left-[-2px] h-[45px] w-[45px] rounded-bl-[15px] border-[4px] border-r-0 border-t-0 border-white" />
                </>
              )}
              {placeholderImage}
            </div>
          )}
          <canvas ref={canvasRef} className="hidden" />
          {isLoading ? (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-white/30 border-t-white" />
            </div>
          ) : (
            <ReactWebcam
              ref={webcamRef}
              mirrored={!!mirrored}
              width={props.width}
              height={props.height}
              videoConstraints={constraints}
              imageSmoothing={!!imageSmoothing}
              screenshotFormat={SCREENSHOT_FORMAT}
              onUserMedia={handleUserMediaSuccess}
              screenshotQuality={screenshotQuality}
              onUserMediaError={handleUserMediaErrorWithFallback}
              minScreenshotWidth={minScreenshotWidth}
              minScreenshotHeight={minScreenshotHeight}
              className="webcam__video z-[1] h-full w-full bg-[#e2e2e2] object-cover"
              audio={false}
              disablePictureInPicture={false}
              forceScreenshotSourceSize={false}
            />
          )}
          {children}
        </div>
      );
    },
  ),
);

Webcam.displayName = 'Webcam';

export {Webcam};
